import { BasicAuthenticationClient } from '@frontend/authentication-v2';
import { useLocalStorageState } from '@frontend/common';
import { Application, Feature } from '@frontend/functionality/types';
import { useModals } from '@frontend/modal';
import { SettingKey, SettingValue } from '@frontend/setting/types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '../redux/store';
import { endUserSession } from '../redux/terminal-navigation-slice';
import { useKioskNavigate } from '../utils/kiosk-navigation';
import { useLicenseRepository } from '../utils/license-repository';
import { useActiveKioskInactivityTimeoutOverride } from './kiosk-idle-timer-override.context';

export const IDLE_TIMER_KEY = 'inactivity_timer_enabled';
const DEFAULT_TIME_UNTIL_IDLE_SECONDS = process.env['NX_PUBLIC_BUILD_ENV'] === 'development' ? 1000000 : 60;
const DEFAULT_SHOW_SECONDS = process.env['NX_PUBLIC_BUILD_ENV'] === 'development' && window.location.host.includes('localhost') ? 999990 : 45;

/**
 * @todo use the setInactivityTimeoutOverride on the screens mentioned below (much better then hardcoding it here for every screen)
 * @deprecated
 * Maps a manage-spot route prefix to the (Application, Feature) functionality
 * whose settings should drive the idle timer. Wizards are handled separately
 * via the `:functionalityId` URL parameter.
 */
const MANAGE_SPOT_FUNCTIONALITY_BY_PREFIX: { prefix: string; application: Application; feature: Feature }[] = [
    { prefix: '/spots/layout/pudo', application: Application.PUDO, feature: Feature.MANAGE },
    { prefix: '/spots/layout/vending-stock', application: Application.VENDING, feature: Feature.STOCK },
    { prefix: '/spots/layout/lending-stock', application: Application.LENDING, feature: Feature.STOCK },
    { prefix: '/spots/layout/slot', application: Application.SPOTS, feature: Feature.LAYOUT },
    { prefix: '/spots/layout/open-slots', application: Application.SPOTS, feature: Feature.LAYOUT },
    { prefix: '/spots/layout/view-content', application: Application.SPOTS, feature: Feature.LAYOUT },
    { prefix: '/spots/layout', application: Application.SPOTS, feature: Feature.LAYOUT }
];

const WIZARD_ROUTE_PREFIX = '/wizard/';

const toPositiveNumber = (value: SettingValue | undefined): number | undefined => {
    if (typeof value === 'number' && !isNaN(value) && value > 0) return value;
    if (typeof value === 'string' && value.trim() !== '') {
        const parsed = Number(value);
        if (!isNaN(parsed) && parsed > 0) return parsed;
    }
    return undefined;
};

interface ViewProps {
    enabled: boolean;
    almostInActive: boolean;
    timeUntilInactive: number;
    onClick: (e: any) => void;
}

const useKioskIdleTimer = (): ViewProps => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const navigate = useKioskNavigate();
    const navigationState = useAppSelector((store) => store.navigation);
    const { allFunctionalities, getFunctionality, getFunctionalitySetting } = useLicenseRepository();
    const { closeAllModals } = useModals();
    const activeOverride = useActiveKioskInactivityTimeoutOverride();

    const [lastActivity, changeLastActivity] = useState(new Date());
    const [tickDate, changeTickDate] = useState(Date.now());
    const [enabled, changeEnabled] = useLocalStorageState(IDLE_TIMER_KEY, false);

    const activeFunctionalityId = useMemo(() => {
        const pathname = location.pathname;
        if (pathname.startsWith(WIZARD_ROUTE_PREFIX)) {
            const id = pathname.slice(WIZARD_ROUTE_PREFIX.length).split('/')[0];
            return allFunctionalities.find((f) => f.id === id)?.id;
        }
        const match = MANAGE_SPOT_FUNCTIONALITY_BY_PREFIX.find((entry) => pathname.startsWith(entry.prefix));
        if (!match) return undefined;
        return getFunctionality(match.application, match.feature)?.id;
    }, [allFunctionalities, location.pathname, getFunctionality]);

    const { timeUntilIdleSeconds, showSeconds } = useMemo(() => {
        if (activeOverride) {
            const idle = activeOverride.idleSeconds;
            const modal = activeOverride.modalSeconds ?? DEFAULT_SHOW_SECONDS;
            const safeModal = modal >= idle ? Math.max(idle - 1, 0) : modal;
            return { timeUntilIdleSeconds: idle, showSeconds: safeModal };
        }

        const functionalitySettings = activeFunctionalityId ? getFunctionalitySetting(activeFunctionalityId) : {};
        const idle = toPositiveNumber(functionalitySettings[SettingKey.INACTIVITY_TIMEOUT_SECONDS]) ?? DEFAULT_TIME_UNTIL_IDLE_SECONDS;
        const modal = toPositiveNumber(functionalitySettings[SettingKey.INACTIVITY_TIMEOUT_MODAL_SECONDS]) ?? DEFAULT_SHOW_SECONDS;
        // Modal must trigger before the idle timeout. If misconfigured, clamp to idle - 1.
        const safeModal = modal >= idle ? Math.max(idle - 1, 0) : modal;
        return { timeUntilIdleSeconds: idle, showSeconds: safeModal };
    }, [activeFunctionalityId, activeOverride, getFunctionalitySetting]);

    const inActive = tickDate - lastActivity.getTime() > timeUntilIdleSeconds * 1000;
    const almostInActive = tickDate - lastActivity.getTime() > showSeconds * 1000;
    const timeUntilInactive = Math.ceil((timeUntilIdleSeconds * 1000 - (tickDate - lastActivity.getTime())) / 1000);

    const enabledRef = useRef(enabled);
    enabledRef.current = enabled;
    const almostInActiveRef = useRef(almostInActive);
    almostInActiveRef.current = almostInActive;
    const handler = (e?: any) => {
        if (enabledRef.current && almostInActiveRef.current) {
            e.preventDefault();
            e.stopPropagation();
        }
        changeLastActivity(new Date());
    };

    useEffect(() => {
        let timerId: number | undefined = undefined;
        if (enabled) {
            handler();
            addHandlers(handler);
            changeTickDate(Date.now());
            timerId = window.setInterval(() => {
                changeTickDate(Date.now());
            }, 500);
        }
        return () => {
            if (timerId !== undefined) window.clearInterval(timerId);
            removeHandlers(handler);
        };
    }, [enabled]);

    useEffect(() => {
        if (enabled && inActive && tickDate != 0) {
            changeTickDate(0);
            closeAllModals();
            navigate('/');
            if (navigationState.user?.auth.jwt_token) {
                BasicAuthenticationClient.logout(navigationState.user.auth.jwt_token);
            }
            dispatch(endUserSession());
        }
    }, [inActive, enabled]);

    useEffect(() => {
        changeEnabled(location.pathname !== '/' || navigationState.user != null);
    }, [location.pathname, navigationState.user]);

    const onClick = (e: any) => {
        e.stopPropagation();
        e.preventDefault();
        triggerActivity();
    };

    return {
        enabled,
        almostInActive,
        timeUntilInactive,
        onClick
    };
};

export default useKioskIdleTimer;

function addHandlers(handler: (e?: any) => void) {
    window.addEventListener('touchend', handler);
    window.addEventListener('kiosk_activity', handler);
}

function removeHandlers(handler: (e?: any) => void) {
    window.removeEventListener('touchend', handler);
    window.removeEventListener('kiosk_activity', handler);
}
/**
 * Function used to reset idle timer.
 */
export function triggerActivity() {
    window.dispatchEvent(new CustomEvent('kiosk_activity'));
}
